import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { contentAdminApi } from "../../../lib/api";
import { PAGE_LABELS, SITE_TABS } from "./blockSchemas";
import { BlockStatusBadge, relativeTime } from "./BlockStatusBadge";
import {
  Globe, LayoutDashboard, Home, Info, Store, Calendar, Mail, Handshake,
  AlignEndVertical, Menu, Type, Search, RefreshCw, ArrowRight, Loader2,
  LogOut, ExternalLink,
} from "lucide-react";
import { useAuth } from "../../../lib/AuthContext";

const ICON_MAP = {
  LayoutDashboard, Home, Info, Store, Calendar, Mail, Handshake,
  AlignEndVertical, Menu, Type, Search,
};

/** Top sub-nav bar (tabs). */
export function SiteTabs({ activeKey }) {
  return (
    <div className="sticky top-0 z-30 bg-white border-b border-slate-200">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-1 overflow-x-auto no-scrollbar py-3">
          {SITE_TABS.map((tab) => {
            const Icon = ICON_MAP[tab.icon] || Globe;
            const to = tab.key === "dashboard" ? "/admin/sito" : `/admin/sito/${tab.key}`;
            const isActive = activeKey === tab.key;
            return (
              <Link
                key={tab.key}
                to={to}
                className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                  isActive
                    ? "bg-[#12964A] text-white"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
                data-testid={`site-tab-${tab.key}`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/** Site Web header — brand + back link + logout. */
export function SiteHeader() {
  const { logout } = useAuth() || {};
  const navigate = useNavigate();
  return (
    <header className="bg-white border-b border-slate-200">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/admin" className="flex items-center gap-3 text-slate-900" data-testid="link-back-admin">
          <Globe className="w-5 h-5 text-[#12964A]" />
          <span className="font-display font-bold text-lg">Sito Web</span>
          <span className="text-xs text-slate-400 hidden sm:inline">· CMS editoriale</span>
        </Link>
        <div className="flex items-center gap-3">
          <Link to="/" target="_blank" className="text-sm text-slate-600 hover:text-[#12964A] inline-flex items-center gap-1">
            Anteprima sito <ExternalLink className="w-3.5 h-3.5" />
          </Link>
          <button
            onClick={async () => { if (logout) await logout(); navigate("/admin/login"); }}
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-red-600"
            data-testid="btn-site-logout"
          >
            <LogOut className="w-4 h-4" /> Esci
          </button>
        </div>
      </div>
    </header>
  );
}

/** Dashboard: KPI + site map + recent activity. */
export default function SiteDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await contentAdminApi.dashboard();
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore caricamento dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const kpi = data?.kpi || {};

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteHeader />
      <SiteTabs activeKey="dashboard" />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        {/* Title */}
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-2">Sito Web</div>
            <h1 className="font-display font-bold text-3xl md:text-4xl text-[#1F2937]">Dashboard editoriale</h1>
            <p className="text-slate-500 mt-1 text-sm">Panoramica dello stato dei contenuti del sito.</p>
          </div>
          <button
            onClick={load}
            className="inline-flex items-center gap-2 border border-slate-200 rounded-full px-4 py-2 text-sm hover:border-slate-400"
            data-testid="btn-dashboard-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Aggiorna
          </button>
        </div>

        {error && <div className="mb-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</div>}

        {loading && !data ? (
          <div className="flex items-center justify-center py-24 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : data ? (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
              <KpiCard label="Pagine" value={kpi.pages} testid="kpi-pages" />
              <KpiCard label="Blocchi totali" value={kpi.blocks_total} testid="kpi-blocks-total" />
              <KpiCard label="Pubblicati" value={kpi.published} tone="emerald" testid="kpi-published" />
              <KpiCard label="Bozze" value={kpi.drafts} tone="yellow" testid="kpi-drafts" />
              <KpiCard label="Fallback" value={kpi.fallback} tone="slate" testid="kpi-fallback" />
              <KpiCard label="Mancanti" value={kpi.missing} tone="red" testid="kpi-missing" />
            </div>

            {/* Site map — cards per page */}
            <div className="mb-8">
              <h2 className="font-display font-semibold text-lg text-slate-900 mb-3">Mappa del sito</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {(data.pages || []).map((p) => (
                  <PageCard key={p.page_key} page={p} />
                ))}
              </div>
            </div>

            {/* Recent activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ActivityCard title="🕐 Ultime modifiche" items={data.recent_activity || []} kind="activity" />
              <ActivityCard title="✅ Ultime pubblicazioni" items={data.recent_publications || []} kind="publications" />
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function KpiCard({ label, value, tone = "default", testid }) {
  const tones = {
    default: "text-slate-900",
    emerald: "text-emerald-600",
    yellow: "text-yellow-600",
    slate: "text-slate-500",
    red: "text-red-600",
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 px-4 py-4" data-testid={testid}>
      <div className={`font-display font-bold text-3xl ${tones[tone]}`}>{value ?? "—"}</div>
      <div className="text-xs text-slate-500 uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}

function PageCard({ page }) {
  const stats = useMemo(() => {
    const s = { published: 0, draft: 0, fallback: 0, missing: 0 };
    (page.blocks || []).forEach((b) => { s[b.state] = (s[b.state] || 0) + 1; });
    return s;
  }, [page]);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid={`page-card-${page.page_key}`}>
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
        <div>
          <div className="text-xs text-slate-400 uppercase tracking-wider">{page.page_key}</div>
          <div className="font-semibold text-slate-900">{PAGE_LABELS[page.page_key] || page.title || page.page_key}</div>
        </div>
        <Link
          to={`/admin/sito/${page.page_key}`}
          className="inline-flex items-center gap-1 text-sm text-[#12964A] hover:underline"
          data-testid={`link-page-${page.page_key}`}
        >
          Apri <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      <div className="p-3 space-y-1.5">
        {(page.blocks || []).slice(0, 8).map((b) => (
          <div key={`${b.block_key}-${b.id || "missing"}`} className="flex items-center justify-between text-sm">
            <span className="text-slate-700 truncate">{b.block_key}</span>
            <BlockStatusBadge state={b.state} is_visible={b.is_visible} />
          </div>
        ))}
        {(page.blocks || []).length > 8 && (
          <div className="text-xs text-slate-400 pt-1">+ altri {page.blocks.length - 8}</div>
        )}
      </div>
      <div className="px-4 py-2 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500 flex items-center gap-3">
        <span>🟢 {stats.published}</span>
        <span>🟡 {stats.draft}</span>
        <span>⚪ {stats.fallback}</span>
        {stats.missing > 0 && <span className="text-red-600">🔴 {stats.missing}</span>}
      </div>
    </div>
  );
}

function ActivityCard({ title, items, kind }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <h3 className="font-semibold text-sm text-slate-900 mb-3">{title}</h3>
      {items.length === 0 ? (
        <div className="text-sm text-slate-400 py-8 text-center">Nessuna attività recente.</div>
      ) : (
        <ul className="space-y-2.5">
          {items.slice(0, 10).map((it) => (
            <li key={it.id} className="text-sm text-slate-700 flex items-start gap-2">
              <span className="text-slate-400 text-xs shrink-0 mt-0.5" title={it.created_at}>{relativeTime(it.created_at)}</span>
              <span className="flex-1 truncate">
                <span className="font-medium">{it.operation}</span>
                {" · "}
                <span className="text-slate-500">{it.entity_type}</span>
                {" · "}
                <span className="text-slate-500">{it.user_email || "—"}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
